<script setup lang="ts">
import {
  ContentBreak,
  ContentImage,
  ContentText,
  ContentTitle,
  ContentVideo,
} from "#components";

const emit = defineEmits<{
  shareImage: [item?: GalleryItem];
  likeImage: [item?: GalleryItem];
  zoomImage: [item?: GalleryItem];
}>();

const props = defineProps<{ post: PostItem }>();

const { isCrawler } = useUserDevice();

const expanded = ref(isCrawler ? true : false);

const sections = computed<
  {
    component: Component;
    props: Record<string, any>;
  }[]
>(() => {
  return (
    props.post?.content?.map((content) => {
      switch (content.collection) {
        case "images":
          return {
            component: ContentImage,
            props: {
              provider: "directus",
              src:
                content.item?.file?.id +
                (content.item?.file?.filename_download
                  ? "/" +
                    encodeURIComponent(content.item.file.filename_download)
                  : ""),
              width: content.item?.file?.width,
              height: content.item?.file?.height,
              alt:
                content.item?.file?.description ||
                content.item?.caption ||
                props.post.title,
              caption: content.item?.caption,

              imageId: content.item?.id,

              sizes:
                "100vw xs:390px sm:640px md:768px lg:455px xl:640px xxl:880px",

              onShare: () =>
                emit(
                  "shareImage",
                  content.item
                    ? { collection: "images", item: content.item }
                    : undefined
                ),
              onLike: () =>
                emit(
                  "likeImage",
                  content.item
                    ? { collection: "images", item: content.item }
                    : undefined
                ),
              onZoom: () =>
                emit(
                  "zoomImage",
                  content.item
                    ? { collection: "images", item: content.item }
                    : undefined
                ),
            },
          };
        case "videos":
          return {
            component: ContentVideo,
            props: {
              provider: "directus",
              src:
                content.item?.file?.id +
                (content.item?.file?.filename_download
                  ? "/" +
                    encodeURIComponent(content.item.file.filename_download)
                  : ""),
              width: content.item?.file?.width,
              height: content.item?.file?.height,
              caption: content.item?.caption,
              type: content.item?.file?.type,
              poster: content.item?.poster?.id,

              onZoom: () =>
                emit(
                  "zoomImage",
                  content.item
                    ? { collection: "videos", item: content.item }
                    : undefined
                ),
            },
          };
        case "content_text":
          return {
            component: ContentText,
            props: {
              value: content.item?.value,
            },
          };
        case "content_title":
          return {
            component: ContentTitle,
            props: {
              level: content.item?.level,
              type: content.item?.type,
              normal: content.item?.value_normal,
              decorative: content.item?.value_decorative,
            },
          };
        case "content_break":
          return expanded.value
            ? null
            : {
                component: ContentBreak,
                props: {
                  onExpand: () => {
                    expanded.value = true;
                  },
                },
              };
        default:
          return null;
      }
    }) ?? []
  )?.filter((section) => section !== null);
});

const breakIndex = computed(() => {
  return sections.value.findIndex(
    (section) => section.component === ContentBreak
  );
});

const hasBreak = computed(() => {
  return breakIndex.value >= 0;
});
</script>

<template>
  <div class="post-content-builder relative">
    <div
      class="post-content-builder__wrapper flex flex-col items-stretch gap-5"
    >
      <template v-for="(item, index) in sections">
        <div
          class="post-content-builder__item"
          :aria-expanded="hasBreak && index > breakIndex ? expanded : undefined"
          :tabindex="hasBreak && index > breakIndex ? '-1' : undefined"
          v-show="hasBreak && index > breakIndex + 1 ? expanded : true"
          :class="[
            {
              'absolute bottom-0 inset-x-0 z-20':
                item.component === ContentBreak,
            },
            ...(hasBreak && index === breakIndex + 1
              ? [
                  'h-24 overflow-hidden relative pointer-events-none',
                  `after:content-['']`,
                  'after:block after:absolute after:bg-gradient-to-t after:from-white after:via-white after:to-transparent after:inset-0 after:h-full after:w-full after:z-10',
                ]
              : []),
          ]"
        >
          <component :is="item.component" v-bind="item.props" />
        </div>
      </template>
    </div>
  </div>
</template>
